import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchLeague, fetchDivisionsForSeason } from '../../store/slices/leagueSlice';
import {
  fetchSeasonEnrollments,
  placeEnrollment,
  withdrawEnrollment,
} from '../../store/slices/enrollmentSlice';
import { UserRole, DivisionStatus, EnrollmentStatus } from '@sulis-liiga/common';
import type { Enrollment } from '@sulis-liiga/common';
import { CATEGORY_SLUGS, CATEGORY_TO_SLUG } from './DivisionsPage';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import leagueService from '../../services/leagueService';

// ─── ManageDivisionsPage ──────────────────────────────────────────────────────

const ManageDivisionsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { leagueId, category: categorySlug } = useParams<{
    leagueId: string;
    category: string;
  }>();
  const [searchParams] = useSearchParams();
  // Optional ?seasonId= to manage a specific (e.g. upcoming) season's divisions.
  const querySeasonId = searchParams.get('seasonId') ?? undefined;

  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const {
    currentLeague,
    currentSeason,
    divisions,
    loading: leagueLoading,
    error: leagueError,
  } = useSelector((state: RootState) => state.league);
  const {
    seasonEnrollments,
    loading: enrollLoading,
    error: enrollError,
  } = useSelector((state: RootState) => state.enrollment);

  // Per-enrollment tier selections (enrollmentId → divisionId)
  const [tierSelections, setTierSelections] = useState<Record<string, string>>({});
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});

  // Teams enrolled while no upcoming season existed — season-less, awaiting the
  // next upcoming season (read-only, no placement controls here).
  const [queuedEnrollments, setQueuedEnrollments] = useState<Enrollment[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);

  // The season being managed (may be a specific upcoming season via ?seasonId=).
  const seasonId = querySeasonId ?? currentSeason?.id ?? currentLeague?.currentSeasonId;

  // ─── RBAC gate ────────────────────────────────────────────────────────────

  const isAdmin =
    isAuthenticated &&
    (user?.roles?.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
      user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR));

  // ─── Tenant + data loading ────────────────────────────────────────────────

  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  useEffect(() => {
    if (!isAdmin || !leagueId || !tenantResolved) return;
    dispatch(fetchLeague(leagueId));
  }, [dispatch, leagueId, isAdmin, tenantResolved]);

  // Load divisions so the admin can pick a tier for placement.
  useEffect(() => {
    if (!isAdmin || !leagueId || !tenantResolved || !seasonId) return;
    dispatch(fetchDivisionsForSeason({ leagueId, seasonId }));
  }, [dispatch, leagueId, seasonId, isAdmin, tenantResolved]);

  // Fetch ALL of the season's enrollments (not just pending) so the admin can
  // also re-position already-placed teams while their division is upcoming.
  const refetchEnrollments = useCallback(() => {
    if (!leagueId || !seasonId) return;
    dispatch(fetchSeasonEnrollments({ leagueId, seasonId }));
  }, [dispatch, leagueId, seasonId]);

  useEffect(() => {
    if (!isAdmin || !leagueId || !tenantResolved || !seasonId) return;
    refetchEnrollments();
  }, [isAdmin, leagueId, tenantResolved, seasonId, refetchEnrollments]);

  // Load the league-wide queue (season-less enrollments, awaiting the next
  // upcoming season) — independent of the currently-managed season.
  useEffect(() => {
    if (!isAdmin || !leagueId || !tenantResolved) return;
    let cancelled = false;
    setQueueError(null);
    leagueService
      .getLeagueQueue(leagueId)
      .then(data => {
        if (!cancelled) setQueuedEnrollments(data ?? []);
      })
      .catch(() => {
        if (!cancelled) setQueueError('Failed to load the queued teams.');
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, leagueId, tenantResolved]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  // Place a pending enrollment, or re-position an already-placed one, into the
  // selected tier. Both use placeEnrollment; the backend blocks re-positioning
  // once the division is active.
  const handlePlace = async (enrollment: Enrollment) => {
    const divisionId = tierSelections[enrollment.id] ?? enrollment.divisionId;
    if (!divisionId) {
      setActionErrors(prev => ({ ...prev, [enrollment.id]: 'Please select a tier first.' }));
      return;
    }
    setActionErrors(prev => ({ ...prev, [enrollment.id]: '' }));
    const result = await dispatch(placeEnrollment({ id: enrollment.id, divisionId }));
    if (placeEnrollment.fulfilled.match(result)) {
      refetchEnrollments();
      // The queue is tracked in local state (not the enrollment slice), so a
      // successfully-placed queued row must be removed here or it would linger
      // in "Queued for next season" until the next full page load.
      setQueuedEnrollments(prev => prev.filter(q => q.id !== enrollment.id));
    } else {
      setActionErrors(prev => ({
        ...prev,
        [enrollment.id]: (result.payload as string) || 'Failed to place enrollment.',
      }));
    }
  };

  const handleWithdraw = async (enrollmentId: string) => {
    await dispatch(withdrawEnrollment(enrollmentId));
    refetchEnrollments();
  };

  // ─── Computed ────────────────────────────────────────────────────────────

  // Tiers in the same group as the enrollment's divisionGroupId.
  const tiersForEnrollment = (enrollment: Enrollment) =>
    divisions
      .filter(
        d =>
          (enrollment.divisionGroupId
            ? d.divisionGroupId === enrollment.divisionGroupId
            : d.category === enrollment.category) && d.seasonId === enrollment.seasonId,
      )
      .sort((a, b) => a.level - b.level);

  // A queued enrollment is season-less, so it can't be matched by divisionGroupId
  // (regenerated per season) or seasonId (absent) — match it to the *managed*
  // season's ladder by category + base name instead.
  const tiersForQueued = (enrollment: Enrollment) =>
    divisions
      .filter(
        d =>
          d.category === enrollment.category &&
          (d.baseName ?? d.name) === enrollment.divisionBaseName &&
          d.seasonId === seasonId,
      )
      .sort((a, b) => a.level - b.level);

  // Whether the enrollment's ladder has gone active — once it has, a team's tier
  // is locked (games have been generated).
  const isLadderActive = (enrollment: Enrollment) =>
    tiersForEnrollment(enrollment).some(d => d.status === DivisionStatus.ACTIVE);

  // The queue is per-category (route .../divisions/:category/manage), so only
  // show enrollments for this category.
  const pageCategory = categorySlug ? CATEGORY_SLUGS[categorySlug] : undefined;
  const inCategory = (e: Enrollment) => !pageCategory || e.category === pageCategory;

  const pendingEnrollments = seasonEnrollments.filter(
    e => inCategory(e) && e.status === EnrollmentStatus.PENDING_PLACEMENT,
  );
  const placedEnrollments = seasonEnrollments.filter(
    e => inCategory(e) && e.status === EnrollmentStatus.ACTIVE,
  );
  const queuedForCategory = queuedEnrollments.filter(inCategory);

  const isLoading = leagueLoading || enrollLoading;

  // ─── Access denied ────────────────────────────────────────────────────────

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto mt-16 text-center">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access denied</h2>
          <p className="text-gray-600">
            You must be a League Administrator or Super Administrator to manage divisions.
          </p>
          <Link
            to={`/leagues/${leagueId}/divisions/${categorySlug ?? 'mens-singles'}`}
            className="mt-4 inline-block text-primary-600 hover:underline"
          >
            &larr; Back to Divisions
          </Link>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Back link */}
      <Link
        to={`/leagues/${leagueId}/divisions/${
          categorySlug
            ? CATEGORY_TO_SLUG[CATEGORY_SLUGS[categorySlug]] ?? categorySlug
            : 'mens-singles'
        }`}
        className="text-sm text-primary-600 hover:underline"
      >
        &larr; Back to Divisions
      </Link>

      {/* Page header */}
      <div className="mt-2 pb-4 border-b border-gray-200 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Enrollment Placement Queue</h1>
          <p className="text-sm text-gray-500 mt-1">
            {currentLeague?.name ?? '…'}
            {currentSeason ? ` · ${currentSeason.name}` : ''}
          </p>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-violet-600 text-white">Admin view</span>
      </div>

      {(leagueError || enrollError) && (
        <div className="mt-4 rounded-md bg-danger-50 p-3" role="alert">
          <p className="text-sm text-danger-600">{leagueError || enrollError}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <svg
            className="animate-spin h-8 w-8 text-primary-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-label="Loading"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      ) : (
        <>
          {/* ── Pending-placement enrollments ─────────────────────────── */}
          <section className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-medium text-gray-900">
                Pending placement
                <span className="ml-2 text-sm text-gray-400 font-normal">
                  · assign a tier to place the team
                </span>
              </h2>
            </div>

            {pendingEnrollments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center text-gray-500 text-sm">
                No enrollments awaiting placement.
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Pending enrollments">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Team</th>
                      <th className="text-left px-4 py-2 font-medium">Ladder</th>
                      <th className="text-left px-4 py-2 font-medium">Category</th>
                      <th className="text-left px-4 py-2 font-medium">Tier</th>
                      <th className="px-4 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pendingEnrollments.map(enrollment => {
                      const tiers = tiersForEnrollment(enrollment);
                      return (
                        <tr key={enrollment.id}>
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {enrollment.teamName ?? enrollment.teamId}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {enrollment.divisionBaseName ?? enrollment.divisionGroupId}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{enrollment.category}</td>
                          <td className="px-4 py-3">
                            {tiers.length === 0 ? (
                              <span className="text-xs text-gray-400">No tiers available</span>
                            ) : (
                              <select
                                className="rounded-md border border-gray-300 text-sm px-2 py-1 bg-white"
                                value={tierSelections[enrollment.id] ?? ''}
                                onChange={e =>
                                  setTierSelections(prev => ({
                                    ...prev,
                                    [enrollment.id]: e.target.value,
                                  }))
                                }
                                aria-label={`Select tier for ${
                                  enrollment.teamName ?? enrollment.teamId
                                }`}
                              >
                                <option value="">Choose tier…</option>
                                {tiers.map(div => (
                                  <option key={div.id} value={div.id}>
                                    {div.name}
                                  </option>
                                ))}
                              </select>
                            )}
                            {actionErrors[enrollment.id] && (
                              <p className="mt-1 text-xs text-danger-600" role="alert">
                                {actionErrors[enrollment.id]}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handlePlace(enrollment)}
                                disabled={enrollLoading || !tierSelections[enrollment.id]}
                                className="px-3 py-1.5 rounded-md text-xs font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 disabled:opacity-50"
                              >
                                Place
                              </button>
                              <button
                                type="button"
                                onClick={() => handleWithdraw(enrollment.id)}
                                disabled={enrollLoading}
                                className="px-3 py-1.5 rounded-md text-xs font-medium text-danger-600 border border-danger-300 hover:bg-danger-50 disabled:opacity-50"
                                aria-label={`Withdraw enrollment for ${
                                  enrollment.teamName ?? enrollment.teamId
                                }`}
                              >
                                Withdraw
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-2 text-xs text-gray-400">
              Placing a team assigns it to a tier. Withdrawing removes the enrollment.
            </p>
          </section>

          {/* ── Placed teams (re-position while upcoming) ─────────────────── */}
          <section className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-medium text-gray-900">
                Placed teams
                <span className="ml-2 text-sm text-gray-400 font-normal">
                  · re-position between tiers until the division goes active
                </span>
              </h2>
            </div>

            {placedEnrollments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center text-gray-500 text-sm">
                No teams placed yet.
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Placed enrollments">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Team</th>
                      <th className="text-left px-4 py-2 font-medium">Ladder</th>
                      <th className="text-left px-4 py-2 font-medium">Tier</th>
                      <th className="px-4 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {placedEnrollments.map(enrollment => {
                      const tiers = tiersForEnrollment(enrollment);
                      const locked = isLadderActive(enrollment);
                      const selection =
                        tierSelections[enrollment.id] ?? enrollment.divisionId ?? '';
                      return (
                        <tr key={enrollment.id}>
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {enrollment.teamName ?? enrollment.teamId}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {enrollment.divisionBaseName ?? enrollment.divisionGroupId}
                          </td>
                          <td className="px-4 py-3">
                            {locked ? (
                              <span className="text-gray-700">
                                {enrollment.divisionName ?? '—'}
                                <span className="ml-2 text-xs text-gray-400">
                                  · locked (active)
                                </span>
                              </span>
                            ) : (
                              <select
                                className="rounded-md border border-gray-300 text-sm px-2 py-1 bg-white"
                                value={selection}
                                onChange={e =>
                                  setTierSelections(prev => ({
                                    ...prev,
                                    [enrollment.id]: e.target.value,
                                  }))
                                }
                                aria-label={`Change tier for ${
                                  enrollment.teamName ?? enrollment.teamId
                                }`}
                              >
                                {tiers.map(div => (
                                  <option key={div.id} value={div.id}>
                                    {div.name}
                                  </option>
                                ))}
                              </select>
                            )}
                            {actionErrors[enrollment.id] && (
                              <p className="mt-1 text-xs text-danger-600" role="alert">
                                {actionErrors[enrollment.id]}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {!locked && (
                                <button
                                  type="button"
                                  onClick={() => handlePlace(enrollment)}
                                  disabled={enrollLoading || selection === enrollment.divisionId}
                                  className="px-3 py-1.5 rounded-md text-xs font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 disabled:opacity-50"
                                >
                                  Update tier
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleWithdraw(enrollment.id)}
                                disabled={enrollLoading}
                                className="px-3 py-1.5 rounded-md text-xs font-medium text-danger-600 border border-danger-300 hover:bg-danger-50 disabled:opacity-50"
                                aria-label={`Withdraw enrollment for ${
                                  enrollment.teamName ?? enrollment.teamId
                                }`}
                              >
                                Withdraw
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Queued for next season (read-only) ────────────────────── */}
          <section className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-medium text-gray-900">
                Queued for next season
                <span className="ml-2 text-sm text-gray-400 font-normal">
                  · season-less, awaiting the next upcoming season
                </span>
              </h2>
            </div>

            {queueError && (
              <div className="mb-2 rounded-md bg-danger-50 p-3" role="alert">
                <p className="text-sm text-danger-600">{queueError}</p>
              </div>
            )}

            {queuedForCategory.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center text-gray-500 text-sm">
                No teams queued.
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Queued enrollments">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Team</th>
                      <th className="text-left px-4 py-2 font-medium">Ladder</th>
                      <th className="text-left px-4 py-2 font-medium">Tier</th>
                      <th className="px-4 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {queuedForCategory.map(enrollment => {
                      const tiers = tiersForQueued(enrollment);
                      return (
                        <tr key={enrollment.id}>
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {enrollment.teamName ?? enrollment.teamId}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {enrollment.divisionBaseName ?? enrollment.divisionGroupId}
                          </td>
                          <td className="px-4 py-3">
                            {tiers.length === 0 ? (
                              <span className="text-xs text-gray-400">
                                No matching tier in this season
                              </span>
                            ) : (
                              <select
                                className="rounded-md border border-gray-300 text-sm px-2 py-1 bg-white"
                                value={tierSelections[enrollment.id] ?? ''}
                                onChange={e =>
                                  setTierSelections(prev => ({
                                    ...prev,
                                    [enrollment.id]: e.target.value,
                                  }))
                                }
                                aria-label={`Select tier for ${
                                  enrollment.teamName ?? enrollment.teamId
                                }`}
                              >
                                <option value="">Choose tier…</option>
                                {tiers.map(div => (
                                  <option key={div.id} value={div.id}>
                                    {div.name}
                                  </option>
                                ))}
                              </select>
                            )}
                            {actionErrors[enrollment.id] && (
                              <p className="mt-1 text-xs text-danger-600" role="alert">
                                {actionErrors[enrollment.id]}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => handlePlace(enrollment)}
                              disabled={
                                enrollLoading ||
                                tiers.length === 0 ||
                                !tierSelections[enrollment.id]
                              }
                              className="px-3 py-1.5 rounded-md text-xs font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 disabled:opacity-50"
                            >
                              Place
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-2 text-xs text-gray-400">
              Queued teams are season-less and normally join the roster automatically once the next
              season is created — but you can place one into a tier of the season being managed here
              right away.
            </p>
          </section>

          {/* ── Edit divisions link ────────────────────────────────────── */}
          <section className="mt-8">
            <p className="text-sm text-gray-500">
              Edit division settings (name, tiers, promotion/relegation) from the{' '}
              <Link to={`/leagues/${leagueId}`} className="text-primary-600 hover:underline">
                league page
              </Link>
              .
            </p>
          </section>
        </>
      )}
    </div>
  );
};

export default ManageDivisionsPage;
